import React, { useState, useCallback } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Box, CircularProgress, Chip,
  TextField, MenuItem, InputAdornment, Button, Card, CardContent, Stack, Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import { ProductService, Product, ProductFilter, ProductError } from '../services/ProductService';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import DataTableCard from '../components/ui/DataTableCard';

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<ProductFilter>({});
  const [hasSearched, setHasSearched] = useState(false);
  const [urunAdi, setUrunAdi] = useState('');
  const [barkod, setBarkod] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const searchProducts = useCallback(async (newFilter: ProductFilter) => {
    if (!newFilter.searchTerm && !newFilter.category && !newFilter.status) {
      setProducts([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const { products: searchResults } = await ProductService.searchProducts(newFilter);
      setProducts(searchResults);
    } catch (error) {
      console.error('Arama sırasında hata:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedSearch = useCallback((value: string) => {
    const timeoutId = setTimeout(() => {
      const newFilter = { ...filter, searchTerm: value };
      setFilter(newFilter);
      searchProducts(newFilter);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [filter, searchProducts]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(event.target.value);
  };

  const handleFilterChange = (key: keyof ProductFilter, value: string) => {
    const newFilter = { ...filter, [key]: value || undefined };
    setFilter(newFilter);
    searchProducts(newFilter);
  };

  const handleAddProduct = async () => {
    if (!urunAdi || !barkod) return;

    setAdding(true);
    setAddError(null);

    try {
      await ProductService.addProduct({ urun_adi: urunAdi, barkod });
      setUrunAdi('');
      setBarkod('');
      setAddError(null);
    } catch (error) {
      const message = error instanceof ProductError
        ? error.message
        : 'Ürün eklenirken bir hata oluştu';
      setAddError(message);
    } finally {
      setAdding(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="280px">
          <CircularProgress />
        </Box>
      );
    }

    if (!hasSearched) {
      return (
        <EmptyState
          icon={<SearchIcon sx={{ fontSize: 36 }} />}
          title="Ürün aramaya başlayın"
          description="Ürün adı veya barkod numarası ile arama yapabilirsiniz. Filtreleri kullanarak sonuçları daraltın."
        />
      );
    }

    if (products.length === 0) {
      return (
        <EmptyState
          icon={<InventoryIcon sx={{ fontSize: 36 }} />}
          title="Sonuç bulunamadı"
          description="Arama kriterlerinize uygun ürün bulunamadı. Farklı bir terim deneyin."
        />
      );
    }

    return (
      <DataTableCard subtitle={`${products.length} ürün listeleniyor`}>
        <TableContainer>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell>Ürün Adı</TableCell>
                <TableCell>Barkod</TableCell>
                <TableCell>Kategori</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Oluşturulma Tarihi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id}>
                  <TableCell component="th" scope="row">
                    <Typography variant="body2" fontWeight={500}>
                      {product.urun_adi}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.barkod || 'Barkod Yok'}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>{product.categoryId || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={product.status || 'Aktif'}
                      color={product.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {product.createdAt ? new Date(product.createdAt).toLocaleDateString('tr-TR') : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DataTableCard>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title="Tüm Ürünler"
        subtitle="Ürün arama, filtreleme ve yeni ürün ekleme"
        icon={<InventoryIcon />}
      />

      <Stack spacing={3} sx={{ mb: 3 }}>
        <Card>
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Arama & Filtreler
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <TextField
                label="Ürün Adı veya Barkod"
                size="small"
                onChange={handleSearchChange}
                sx={{ flex: 1, minWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                label="Kategori"
                size="small"
                value={filter.category || ''}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value="gida">Gıda</MenuItem>
                <MenuItem value="icecek">İçecek</MenuItem>
              </TextField>
              <TextField
                select
                label="Durum"
                size="small"
                value={filter.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value="active">Aktif</MenuItem>
                <MenuItem value="passive">Pasif</MenuItem>
              </TextField>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Yeni Ürün Ekle
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
              <TextField
                label="Ürün Adı"
                value={urunAdi}
                onChange={(e) => setUrunAdi(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Barkod"
                value={barkod}
                onChange={(e) => setBarkod(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddProduct}
                disabled={!urunAdi || !barkod || adding}
                sx={{ minWidth: 140, height: 40 }}
              >
                {adding ? 'Ekleniyor...' : 'Ürün Ekle'}
              </Button>
            </Stack>
            {addError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {addError}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Stack>

      {renderContent()}
    </Box>
  );
};

export default Products;
